import {
  ApiACLRole,
  ApiClient,
  ApiAuthPermission,
  ApiProject,
} from "@incendium/api";
import { ExpandMore } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  capitalize,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/styles";
import { initializeClientsThunk } from "Actions";
import AccessLevel from "Components/AccessLevel/AccessLevel";
import { StyledFlexBox } from "Components/UI/StylesFlexBox";
import { useUser } from "Hooks/useUser";
import { getSelectedEnvironment } from "config";
import produce from "immer";
import { memo, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { clientListSelector } from "Selectors/clientListSelector";

interface IUserRolesProps {
  permissions: ApiAuthPermission[];
  setPermissions: React.Dispatch<React.SetStateAction<ApiAuthPermission[]>>;
  selectedEnvironment?: string | null;
  readOnly?: boolean;
}

interface IFieldsProps {
  toggleAdmin: () => void;
  toggleObs: () => void;
  adminActive: boolean;
  obsActive: boolean;
  type: "client" | "project" | "analytics";
  readOnly?: boolean;
}
interface IRowProps extends React.HTMLAttributes<HTMLDivElement>, IFieldsProps {
  text: string;
}

const Fields = ({
  toggleAdmin,
  toggleObs,
  adminActive,
  obsActive,
  type,
  readOnly,
}: IFieldsProps) => {
  return (
    <Stack direction="row" alignItems={"center"} justifyContent={"flex-end"}>
      <FormControlLabel
        sx={{ minWidth: 160 }}
        disabled={readOnly}
        control={
          <Checkbox
            size="small"
            sx={{ pointerEvents: "none" }}
            onChange={(e) => {
              e.stopPropagation();
              toggleAdmin();
            }}
            checked={adminActive}
          />
        }
        label={capitalize(`${type} Admin`)}
      />
      <FormControlLabel
        sx={{ minWidth: 160 }}
        disabled={readOnly}
        control={
          <Checkbox
            size="small"
            sx={{ pointerEvents: "none" }}
            onChange={(e) => {
              e.stopPropagation();
              toggleObs();
            }}
            checked={obsActive}
          />
        }
        label={capitalize(`${type} Observer`)}
      />
    </Stack>
  );
};

const Row = ({
  text,
  toggleAdmin,
  toggleObs,
  adminActive,
  obsActive,
  children,
  type,
  readOnly,
}: IRowProps) => {
  return (
    <Stack
      sx={{ width: "100%" }}
      direction="row"
      alignItems={"center"}
      justifyContent={"space-between"}
    >
      <Typography color={"secondary"}>{text}</Typography>

      <Stack direction="row" alignItems={"center"}>
        <Box mr={2}>{children}</Box>
        <Fields
          toggleAdmin={toggleAdmin}
          toggleObs={toggleObs}
          adminActive={adminActive}
          obsActive={obsActive}
          type={type}
          readOnly={readOnly}
        />
      </Stack>
    </Stack>
  );
};

interface IPRowProps extends IUserRolesProps {
  project: ApiProject;
  isClientAdmin: boolean;
  isClientObs: boolean;
  permissionsForEnv: ApiAuthPermission[];
}
const ProjectRow = ({
  project,
  permissions,
  setPermissions,
  permissionsForEnv,
  selectedEnvironment,
  isClientAdmin,
  isClientObs,
  readOnly,
}: IPRowProps) => {
  const toggleAdmin = () => {
    toggleRole(ApiACLRole.PROJECT_ADMIN);
  };
  const toggleObs = () => {
    toggleRole(ApiACLRole.PROJECT_OBSERVER);
  };
  const toggleAAdmin = () => {
    toggleRole(ApiACLRole.ANALYTICS_ADMIN);
  };
  const toggleAObs = () => {
    toggleRole(ApiACLRole.ANALYTICS_OBSERVER);
  };
  const toggleRole = (role: ApiACLRole) => {
    setPermissions((p) =>
      produce(p, (draft) => {
        const idx = (draft || []).findIndex(
          (perm) =>
            perm.role === role &&
            perm.projectId === project.id &&
            (perm.environment ?? null) === (selectedEnvironment ?? null),
        );

        if (draft && idx >= 0) {
          draft.splice(idx, 1);
        } else {
          if (!draft) {
            draft = [];
          }
          draft.push({
            role,
            projectId: project.id,
            environment: selectedEnvironment || undefined,
          });
        }
      }),
    );
  };

  const adminActive = useMemo(() => {
    const idx = (permissionsForEnv || []).findIndex(
      (p) => p.role === ApiACLRole.PROJECT_ADMIN && p.projectId === project.id,
    );
    return idx >= 0;
  }, [permissionsForEnv, project]);

  const obsActive = useMemo(() => {
    const idx = (permissionsForEnv || []).findIndex(
      (p) =>
        p.role === ApiACLRole.PROJECT_OBSERVER && p.projectId === project.id,
    );
    return idx >= 0;
  }, [permissionsForEnv, project]);

  const adminAActive = useMemo(() => {
    const idx = (permissionsForEnv || []).findIndex(
      (p) =>
        p.role === ApiACLRole.ANALYTICS_ADMIN && p.projectId === project.id,
    );
    return idx >= 0;
  }, [permissionsForEnv, project]);

  const obsAActive = useMemo(() => {
    const idx = (permissionsForEnv || []).findIndex(
      (p) =>
        p.role === ApiACLRole.ANALYTICS_OBSERVER && p.projectId === project.id,
    );
    return idx >= 0;
  }, [permissionsForEnv, project]);

  return (
    <Stack alignItems={"flex-end"} pr={3}>
      <Row
        text={project.name || ""}
        toggleAdmin={toggleAdmin}
        toggleObs={toggleObs}
        adminActive={adminActive}
        obsActive={obsActive}
        type="project"
        readOnly={readOnly}
      >
        {isClientAdmin && !adminActive && !obsActive && (
          <Typography textAlign={"right"} variant="caption" color="secondary">
            (Admin Inherited)
          </Typography>
        )}
        {isClientObs && !isClientAdmin && !adminActive && !obsActive && (
          <Typography textAlign={"right"} variant="caption" color="secondary">
            (Observer Inherited)
          </Typography>
        )}
      </Row>
      <Fields
        toggleAdmin={toggleAAdmin}
        toggleObs={toggleAObs}
        adminActive={adminAActive}
        obsActive={obsAActive}
        type={"analytics"}
        readOnly={readOnly}
      />
    </Stack>
  );
};

interface ICRowProps extends IUserRolesProps {
  client: ApiClient;
  permissionsForEnv: ApiAuthPermission[];
}
const ClientRow = ({
  client,
  permissions,
  setPermissions,
  permissionsForEnv,
  selectedEnvironment,
  readOnly,
}: ICRowProps) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);

  const toggleAdmin = () => {
    toggleRole(ApiACLRole.CLIENT_ADMIN);
  };
  const toggleObs = () => {
    toggleRole(ApiACLRole.CLIENT_OBSERVER);
  };
  const toggleRole = (role: ApiACLRole) => {
    setPermissions((p) =>
      produce(p, (draft) => {
        const idx = (draft || []).findIndex(
          (perm) =>
            perm.role === role &&
            perm.clientId === client.id &&
            (perm.environment ?? null) === (selectedEnvironment ?? null),
        );

        if (draft && idx >= 0) {
          draft.splice(idx, 1);
        } else {
          if (!draft) {
            draft = [];
          }
          draft.push({
            role,
            clientId: client.id,
            environment: selectedEnvironment || undefined,
          });
        }
      }),
    );
  };

  const adminActive = useMemo(() => {
    const idx = (permissionsForEnv || []).findIndex(
      (p) => p.role === ApiACLRole.CLIENT_ADMIN && p.clientId === client.id,
    );
    return idx >= 0;
  }, [permissionsForEnv, client]);

  const obsActive = useMemo(() => {
    const idx = (permissionsForEnv || []).findIndex(
      (p) => p.role === ApiACLRole.CLIENT_OBSERVER && p.clientId === client.id,
    );
    return idx >= 0;
  }, [permissionsForEnv, client]);

  const projectsWithRoles = useMemo(() => {
    const projectIds = (client.projects || []).map((p) => p.id);
    return (permissionsForEnv || []).filter((p) =>
      projectIds.includes(p.projectId!),
    ).length;
  }, [permissionsForEnv, client]);

  return (
    <Accordion
      sx={{ background: "transparent" }}
      elevation={0}
      disableGutters
      expanded={open}
      id={`client-row-${client.id}`}
      onChange={() => setOpen(!open)}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Row
          text={client.businessName || ""}
          toggleAdmin={toggleAdmin}
          toggleObs={toggleObs}
          adminActive={adminActive}
          obsActive={obsActive}
          type="client"
          readOnly={readOnly}
        >
          {projectsWithRoles > 0 && (
            <Typography variant="caption" color={"secondary"}>
              {projectsWithRoles} project(s) roles set.
            </Typography>
          )}
        </Row>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          background: theme.palette.background.default,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Stack>
          {(client.projects || []).map((p) => (
            <ProjectRow
              key={`${client.id}-${p.id}`}
              project={p}
              permissions={permissions}
              setPermissions={setPermissions}
              permissionsForEnv={permissionsForEnv}
              selectedEnvironment={selectedEnvironment}
              isClientAdmin={adminActive}
              isClientObs={obsActive}
              readOnly={readOnly}
            />
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

function UserRoles({
  permissions,
  setPermissions,
  selectedEnvironment,
  readOnly,
}: IUserRolesProps) {
  const state = useSelector(clientListSelector);
  const { hasPermission } = useUser();
  const dispatch = useDispatch();
  const env = selectedEnvironment ?? getSelectedEnvironment();

  const permissionsForEnv = useMemo(
    () =>
      (permissions || []).filter(
        (p) => (p.environment ?? null) === (env ?? null),
      ),
    [permissions, env],
  );

  useEffect(() => {
    dispatch(initializeClientsThunk());
  }, [dispatch]);

  const isSuper = useMemo(
    () =>
      (permissionsForEnv || []).findIndex(
        (p) => p.role === ApiACLRole.SUPER_ADMIN,
      ) >= 0,
    [permissionsForEnv],
  );
  const isDom = useMemo(
    () =>
      (permissionsForEnv || []).findIndex(
        (p) => p.role === ApiACLRole.DOMAIN_ADMIN,
      ) >= 0,
    [permissionsForEnv],
  );

  const clients = useMemo(() => {
    return state.list
      .map((c) => ({
        ...c,
        projects: (c.projects || []).filter((p) =>
          hasPermission(ApiACLRole.PROJECT_OBSERVER, undefined, c, p),
        ),
      }))
      .filter(
        (c) =>
          hasPermission(ApiACLRole.CLIENT_OBSERVER, undefined, c) ||
          c.projects.length > 0,
      );
  }, [state, hasPermission]);

  if (!state.hasLoaded) {
    return <CircularProgress />;
  }

  return (
    <Stack spacing={1} alignItems="flex-end">
      <Stack p={2} spacing={1} alignItems="flex-end">
        <AccessLevel role={ApiACLRole.SUPER_ADMIN}>
          <StyledFlexBox sx={{ width: 200 }}>
            <Switch
              checked={isSuper}
              disabled={readOnly}
              onChange={(e, checked) =>
                setPermissions((permisions) =>
                  produce(permisions, (draft) => {
                    const idx = (draft || []).findIndex(
                      (p) =>
                        p.role === ApiACLRole.SUPER_ADMIN &&
                        (p.environment ?? null) === (env ?? null),
                    );
                    if (idx >= 0) {
                      draft?.splice(idx, 1);
                    } else {
                      if (!draft) {
                        draft = [];
                      }
                      draft.push({
                        role: ApiACLRole.SUPER_ADMIN,
                        environment: env || undefined,
                      });
                    }
                  }),
                )
              }
            />
            <Typography color={isSuper ? "primary" : "secondary"}>
              Is Super Admin
            </Typography>
          </StyledFlexBox>
        </AccessLevel>

        <AccessLevel role={ApiACLRole.DOMAIN_ADMIN}>
          <StyledFlexBox sx={{ width: 200 }}>
            <Switch
              checked={isDom}
              disabled={readOnly}
              onChange={(e, checked) =>
                setPermissions((permissions) =>
                  produce(permissions, (draft) => {
                    const idx = (draft || []).findIndex(
                      (p) =>
                        p.role === ApiACLRole.DOMAIN_ADMIN &&
                        (p.environment ?? null) === (env ?? null),
                    );
                    if (idx >= 0) {
                      draft?.splice(idx, 1);
                    } else {
                      if (!draft) {
                        draft = [];
                      }
                      draft.push({
                        role: ApiACLRole.DOMAIN_ADMIN,
                        environment: env || undefined,
                      });
                    }
                  }),
                )
              }
            />
            <Typography color={isSuper ? "primary" : "secondary"}>
              Is Domain Admin
            </Typography>
          </StyledFlexBox>
        </AccessLevel>
      </Stack>
      <Box sx={{ width: "100%" }}>
        {clients.map((client) => (
          <ClientRow
            client={client}
            key={client.id}
            permissions={permissions}
            setPermissions={setPermissions}
            permissionsForEnv={permissionsForEnv}
            selectedEnvironment={env}
            readOnly={readOnly}
          />
        ))}
      </Box>
    </Stack>
  );
}

export default memo(UserRoles);
